import { cn } from "@newxsystems/shared";
import type { CSSProperties } from "react";
import { resolveTootPrimitive } from "../../../../client";
import type { TootPrimitiveRegistry } from "../../../../client";
import type { ITootOfficeCardProps } from "./TootOfficeCard.props";

const ContactLine = ({
  ariaLabel,
  href,
  label,
  Link,
  value,
}: {
  ariaLabel?: string;
  href?: string;
  label: string;
  Link?: TootPrimitiveRegistry["Link"];
  value?: string;
}) => {
  if (!value) {
    return null;
  }

  const content =
    href && Link ? (
      <Link
        aria-label={ariaLabel}
        className="min-w-0 break-words text-[color-mix(in_oklab,var(--color-toot-foreground)_92%,transparent)] underline decoration-[color-mix(in_oklab,var(--color-toot-orange)_48%,transparent)] underline-offset-4 transition-colors duration-200 ease-out hover:text-[var(--color-toot-orange)] motion-reduce:transition-none"
        href={href}
      >
        {value}
      </Link>
    ) : (
      <span className="min-w-0 break-words text-[color-mix(in_oklab,var(--color-toot-foreground)_88%,transparent)]">
        {value}
      </span>
    );

  return (
    <div className="grid min-h-9 grid-cols-[4.25rem_minmax(0,1fr)] items-baseline gap-3 border-b border-[color-mix(in_oklab,var(--color-toot-grid-strong)_46%,transparent)] py-2.5 last:border-b-0">
      <dt className="text-[0.65rem] font-black uppercase tracking-[0.18em] text-[var(--color-toot-orange)]">
        {label}
      </dt>
      <dd className="m-0 min-w-0 text-sm leading-6">{content}</dd>
    </div>
  );
};

export const TootOfficeCard = ({
  addressLines,
  className,
  directionsAriaLabel,
  directionsHref,
  directionsLabel,
  directionsRel,
  directionsTarget,
  directionsTitle,
  email,
  emailHref,
  hours,
  image,
  map,
  name,
  phone,
  phoneHref,
  summary,
}: ITootOfficeCardProps) => {
  const Image = resolveTootPrimitive("Image");
  const Link = resolveTootPrimitive("Link");
  const mapSrc = map?.src;
  const hasMap = Boolean(mapSrc);
  const imageSrc = hasMap ? undefined : image?.defaultSrc;
  const imageStyle: CSSProperties | undefined = image?.focalPoint
    ? { objectPosition: `${image.focalPoint.x * 100}% ${image.focalPoint.y * 100}%` }
    : undefined;

  return (
    <article
      className={cn(
        "group/office relative isolate flex h-full min-w-0 flex-col overflow-hidden rounded-[var(--radius-toot-frame)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_78%,transparent)] bg-[radial-gradient(circle_at_100%_0%,rgba(255,77,46,0.12),transparent_15rem),linear-gradient(180deg,var(--color-toot-surface-raised),var(--color-toot-surface-terminal)_72%,var(--color-toot-black))] text-[var(--color-toot-foreground)] shadow-[0_1.25rem_3.5rem_rgba(0,0,0,0.34)] transition-[border-color,box-shadow,transform] duration-300 ease-out hover:-translate-y-0.5 hover:border-[color-mix(in_oklab,var(--color-toot-orange)_58%,var(--color-toot-grid-strong))] hover:shadow-[0_1.5rem_4.5rem_rgba(0,0,0,0.46),0_0_0_1px_rgba(255,77,46,0.13)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,77,46,0.075)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.055)_1px,transparent_1px)] bg-[size:1.75rem_1.75rem] opacity-35 [mask-image:linear-gradient(180deg,black,transparent_94%)]"
      />

      <div className="relative p-3.5 pb-0">
        {imageSrc || hasMap ? (
          <figure className="relative overflow-hidden rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_32%,transparent)] bg-[var(--color-toot-black)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
            <div className="relative aspect-[16/9] overflow-hidden">
              {imageSrc ? (
                <Image
                  alt={image?.alternateText ?? ""}
                  className="h-full w-full object-cover object-center brightness-[0.72] contrast-[1.2] saturate-[0.82] transition-[filter,transform] duration-500 ease-out group-hover/office:scale-[1.025] group-hover/office:brightness-[0.84] group-hover/office:saturate-100 motion-reduce:transition-none motion-reduce:group-hover/office:scale-100"
                  height={image?.height ?? undefined}
                  sizes={image?.sizes ?? "(min-width: 1280px) 50vw, 100vw"}
                  src={imageSrc}
                  srcSet={image?.srcSet ?? undefined}
                  style={imageStyle}
                  width={image?.width ?? undefined}
                />
              ) : hasMap ? (
                <iframe
                  className="absolute inset-0 h-full w-full border-0 bg-[var(--color-toot-black)] brightness-[0.82] contrast-[1.08] saturate-[0.72]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={mapSrc ?? undefined}
                  title={map?.title ?? `${name} location map`}
                />
              ) : null}
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(255,77,46,0.32),transparent_32%),linear-gradient(180deg,rgba(5,5,6,0),rgba(5,5,6,0.68))]"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.08)_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-24 mix-blend-screen"
              />
              {image?.caption ? (
                <figcaption className="absolute inset-x-3 bottom-3 rounded-[var(--radius-toot-panel)] border border-toot-grid/90 bg-toot-surface-raised/90 px-3 py-1.5 text-[0.68rem] font-bold leading-4 text-[color-mix(in_oklab,var(--color-toot-foreground)_76%,transparent)] backdrop-blur-sm">
                  {image.caption}
                </figcaption>
              ) : null}
            </div>
          </figure>
        ) : null}
      </div>

      <div className="relative flex flex-1 flex-col gap-5 p-5 md:p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="mt-2 text-balance text-2xl font-black leading-[1.02] tracking-[-0.045em] text-toot-foreground md:text-[1.7rem]">
              {name}
            </h3>
          </div>
        </div>

        {summary ? (
          <p className="max-w-[58ch] text-sm leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_76%,transparent)] sm:text-[0.95rem] sm:leading-7">
            {summary}
          </p>
        ) : null}

        {addressLines?.length ? (
          <section
            aria-label={`${name} address`}
            className="rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_64%,transparent)] bg-toot-surface-raised/65 p-4"
          >
            <h4 className="text-[0.66rem] font-black uppercase tracking-[0.18em] text-[var(--color-toot-orange)]">
              Address
            </h4>
            <address className="mt-2 not-italic text-sm leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_82%,transparent)]">
              {addressLines.map((line) => (
                <span className="block [overflow-wrap:anywhere]" key={line}>
                  {line}
                </span>
              ))}
            </address>
          </section>
        ) : null}

        {phone || email ? (
          <dl className="m-0 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_64%,transparent)] bg-toot-surface-raised/60 px-4">
            <ContactLine
              ariaLabel={`Call ${name}`}
              href={phoneHref}
              label="Tel"
              Link={Link}
              value={phone}
            />
            <ContactLine
              ariaLabel={`Email ${name}`}
              href={emailHref}
              label="Mail"
              Link={Link}
              value={email}
            />
          </dl>
        ) : null}

        {hours ? (
          <section
            aria-label={`${name} office hours`}
            className="rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_30%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_8%,transparent)] p-4 text-sm leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_82%,transparent)]"
          >
            <h4 className="mb-2 text-[0.66rem] font-black uppercase tracking-[0.18em] text-[var(--color-toot-orange)]">
              Hours
            </h4>
            <div className="[&_ul]:m-0 [&_ul]:list-none [&_ul]:p-0">{hours}</div>
          </section>
        ) : null}

        {directionsHref && directionsLabel && Link ? (
          <Link
            aria-label={directionsAriaLabel}
            className="mt-auto inline-flex min-h-11 w-fit items-center justify-center gap-2 rounded-[var(--radius-toot-panel)] border border-[color-mix(in_oklab,var(--color-toot-orange)_50%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-orange)_11%,transparent)] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[var(--color-toot-orange)] transition-[background-color,border-color,color,transform] duration-200 ease-out hover:-translate-y-0.5 hover:border-[var(--color-toot-red)] hover:bg-[var(--color-toot-red)] hover:text-[var(--color-primary-foreground)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            href={directionsHref}
            rel={directionsRel}
            target={directionsTarget}
            title={directionsTitle}
          >
            {directionsLabel}
            <span aria-hidden="true">→</span>
          </Link>
        ) : null}
      </div>
    </article>
  );
};

TootOfficeCard.displayName = "Contact::TootOfficeCard";
